import { LinearProgress, TextField, makeStyles } from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormLabel from "@material-ui/core/FormLabel";
import Grid from "@material-ui/core/Grid";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import Typography from "@material-ui/core/Typography";
import { Cancel, ImportExport, Save } from "@material-ui/icons";
import { useSnackbar } from "notistack";
import React, { FunctionComponent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router";
import { ActionMeta, ValueType } from "react-select";
import { routesPaths } from "../../Routes";
import {
  useCreateCandidateMutation,
  useDeleteCandidateMutation,
  useExportDebtorNumberMutation,
  useGetCandidateQuery,
  useUpdateCandidateMutation,
} from "../../api/rtk/CandidateApi";
import { useGetPublishedExamsQuery } from "../../api/rtk/ExamApi";
import {
  ExpertRole,
  Language,
  NewEntityId,
  PropertyNames,
  RequiredCandidate,
  RequiredCandidateExpert,
  Switzerland,
  ValidationFields,
} from "../../data/constants";
import ZipCodes from "../../data/zipCodes.json";
import { CandidateViewModel } from "../../model/CandidateViewModel";
import { CourseType } from "../../model/Course";
import { PersonType } from "../../model/PersonType";
import { GetTranslation } from "../../translations/GetTranslation";
import { TranslationKey, isTranslationKey } from "../../translations/TranslationKey";
import { getValidDate } from "../../utils/Dates";
import { AddressFields } from "../common/AddressFields";
import CustomSwitch from "../common/CustomSwitch";
import FormGridCell from "../common/FormGridCell";
import LastModificationInfo from "../common/LastModificationInfo";
import CustomButton from "../common/buttons/CustomButton";
import RightButtonArea from "../common/buttons/RightButtonArea";
import { CompanySelect } from "../common/customAutocomplete/CompanySelect";
import { CountrySelect } from "../common/customAutocomplete/CountrySelect";
import { GenderSelect } from "../common/customAutocomplete/GenderSelect";
import { IPlaceOfOriginOption, PlaceOfOriginSelect } from "../common/customAutocomplete/PlaceOfOriginSelect";
import LanguageCheckboxList from "../common/customCheckBox/LanguageCheckboxList";
import CustomDraggableDialog from "../common/customDialog/CustomDraggableDialog";
import GridTextField from "../common/customGrid/GridTextField";
import { OptionType } from "../common/customSelect/CustomSelect";
import DatePicker from "../common/datePicker/DatePicker";
import AhvNoTextField from "../common/textField/AhvNoTextField";
import CiceroNoTextField from "../common/textField/CiceroNoTextField";
import EmailTextField from "../common/textField/EmailTextField";
import PhoneNoTextField from "../common/textField/PhoneNoTextField";
import { rolesAssignedToCourseTypes, rolesWithRequiredExamOrCourseTypeAssignements } from "../expert/ExpertConstants";
import TranslatedMessage from "../translations/TranslatedMessage";
import { navigateBack } from "./../../utils/Navigation";
import ExpertRolesTable from "./../expert/ExpertRoleExamAssignment/ExpertRolesExamsTable";
import {
  initialCandidate,
  initialRequiredCandidateExpertFields,
  initialRequiredFields,
  initialValidationFields,
} from "./CandidateConstants";
import CandidateCoursesInternal from "./CandidateCoursesInternal";
import { isInvalidZipCode } from "../../utils/ZipCodeUtils";

const useStyles = makeStyles({
  formControl: {
    marginBottom: "-11px",
  },
  expertsRolesTable: {
    paddingBottom: "300px!important",
  },
});

// Candidate form for Internal users, for creation and update
const CandidateForm: FunctionComponent = () => {
  const { candidateId } = useParams<{ candidateId: string }>();
  const intl = useIntl();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const isNewCandidate: boolean = candidateId === NewEntityId;
  const [createCandidate, { isLoading: isCreateCandidateLoading }] = useCreateCandidateMutation();
  const [updateCandidate, { isLoading: isUpdateCandidateLoading }] = useUpdateCandidateMutation();
  const [deleteCandidate, { isLoading: isDeleteCandidateLoading, isSuccess: isDeleteSuccess }] =
    useDeleteCandidateMutation();
  const [exportDebtorNumber, { isLoading: isExportingDebtorNumber }] = useExportDebtorNumberMutation();

  const { data: fetchedCandidate, isFetching: isCandidateFetching } = useGetCandidateQuery(candidateId, {
    skip: isNewCandidate || isDeleteSuccess,
  });
  const { data: exams, isFetching: isExamsFetching } = useGetPublishedExamsQuery();
  const isLoading =
    isCreateCandidateLoading ||
    isUpdateCandidateLoading ||
    isDeleteCandidateLoading ||
    isCandidateFetching ||
    isExamsFetching;

  const [candidate, setCandidate] = useState<CandidateViewModel>(initialCandidate);
  const [isRequired, setIsRequired] = useState<RequiredCandidate & RequiredCandidateExpert>({
    ...initialRequiredFields(),
    ...initialRequiredCandidateExpertFields(),
  });
  const [isValid, setIsValid] = useState<ValidationFields>(initialValidationFields);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [isEmailChangeDialogOpened, setIsEmailChangeDialogOpened] = useState<boolean>(false);
  const [emailChangeNeedsConfirmation, setEmailChangeNeedsConfirmation] = useState<boolean>(false);

  useEffect(() => {
    if (fetchedCandidate !== undefined) {
      setCandidate(fetchedCandidate);
    }
  }, [fetchedCandidate]);

  if (!isLoading && ((fetchedCandidate === undefined && candidateId !== NewEntityId) || exams === undefined)) {
    return <></>;
  }

  const renderForm = () => {
    if (isLoading || isDeleteSuccess || isExportingDebtorNumber) {
      return (
        <Grid item xs={12}>
          <LinearProgress variant="query" />
        </Grid>
      );
    }

    return (
      <>
        {isNewCandidate && renderCreationForm()}
        {!isNewCandidate && renderUpdateForm()}
      </>
    );
  };

  const renderCandidateForm = () => {
    const { privateIsPreferredContact } = candidate;

    return (
      <Grid container={true} spacing={5} direction="column">
        <Grid item={true}>{renderPersonForm()}</Grid>
        <Grid item={true}>{renderBusinessDataForm()}</Grid>
        <Grid item={true}>{renderAddressForm()}</Grid>
        <Grid item={true}>{renderInvoiceForm()}</Grid>
        <Grid item={true}>{!privateIsPreferredContact && renderBusinessForm()}</Grid>
        {!isNewCandidate && (
          <Grid item>
            <LastModificationInfo
              lastModifiedBy={candidate.lastModifiedBy}
              lastModifiedOnUtc={candidate.lastModifiedOnUtc}
            />
          </Grid>
        )}
        <Grid item>
          <CandidateCoursesInternal candidateId={candidate.id} />
          <Grid container spacing={4}>
            <Grid item xs={8}>
              <TextField
                fullWidth
                multiline
                label={<TranslatedMessage id="person.field.internalComment" />}
                value={candidate.internalComment}
                onChange={getChangeHandler(PropertyNames.internalComment)}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>{renderActiveExpertProfileToggle()}</Grid>
        <Grid item>{renderExpertProfile()}</Grid>
      </Grid>
    );
  };

  const renderExpertProfile = () => {
    const { isBankAccountEmpty, hasEmptyRoleArray: rolesWithErrorArray } = isRequired;

    if (candidate.type !== PersonType.Expert) {
      return <></>;
    }

    return (
      <React.Fragment>
        <Grid container spacing={5} direction="column">
          <Grid item={true}>
            <Typography variant="h5">
              <TranslatedMessage id="expert.header" />
            </Typography>
          </Grid>
        </Grid>
        <Grid container spacing={5} direction="column">
          <Grid item={true}>
            <Typography variant="h6">
              <TranslatedMessage id="person.section.expertDetails" />
            </Typography>
          </Grid>
        </Grid>
        <Grid container spacing={5} direction="column">
          <GridTextField
            error={isBankAccountEmpty}
            required
            label={GetTranslation(intl, "person.field.bankAccount")}
            xs={12}
            sm={6}
            md={4}
            onChange={getChangeHandler(PropertyNames.bankAccount)}
            value={candidate.bankAccount || ""}
          />

          <Grid item={true}>
            <Grid container spacing={4}>
              <Grid item={true} xs={12} sm={6} md={4}>
                <LanguageCheckboxList
                  label={GetTranslation(intl, "expert.field.languages")}
                  checkedLanguages={candidate.languages}
                  onSelectedLanguagesChanged={getLanguageChangeHandler()}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item={true} className={classes.expertsRolesTable}>
            <ExpertRolesTable
              currentRolesWithExamsAndCourseTypes={candidate.rolesWithExamsAndCourseTypes}
              getExamChangeHandler={getExamChangeHandler}
              getCourseTypeChangeHandler={getCourseTypeChangeHandler}
              onRoleCreation={onRolesCreation}
              onRoleDelete={onRoleDelete}
              publishedExams={exams || []}
              examSelectErrorsArray={rolesWithErrorArray}
            />
          </Grid>
        </Grid>
      </React.Fragment>
    );
  };

  const renderActiveExpertProfileToggle = () => {
    return (
      <React.Fragment>
        <CustomSwitch
          value={candidate.type === PersonType.Expert}
          onChange={(checked) => handleChange({ type: checked ? PersonType.Expert : PersonType.Candidate })}
          label={<TranslatedMessage id="person.field.activateSpecificProfile" />}
        />
      </React.Fragment>
    );
  };

  const close = () => navigateBack(routesPaths.candidates(), history, history.location);

  const renderCreationForm = () => {
    return (
      <React.Fragment>
        <RightButtonArea>
          <CustomButton tooltip={<TranslatedMessage id="common.save" />} onClick={() => handleCreate()}>
            <Save />
          </CustomButton>
          <CustomButton tooltip={<TranslatedMessage id="common.cancel" />} onClick={close}>
            <Cancel />
          </CustomButton>
        </RightButtonArea>
        {renderCandidateForm()}
      </React.Fragment>
    );
  };

  const renderUpdateForm = () => {
    return (
      <React.Fragment>
        <RightButtonArea>
          {/* Currently disabled as this was causing issue with candidates registered for an exam session */}
          {/* <CustomButton tooltip={<TranslatedMessage id="common.delete" />} onClick={this.openDeleteDialog}>
            <Delete />
          </CustomButton> */}
           <CustomButton tooltip={<TranslatedMessage id="common.export" />} onClick={handleExport}>
            <ImportExport />
          </CustomButton>
          <CustomButton tooltip={<TranslatedMessage id="common.save" />} onClick={() => handleUpdate()}>
            <Save />
          </CustomButton>
          <CustomButton tooltip={<TranslatedMessage id="common.cancel" />} onClick={close}>
            <Cancel />
          </CustomButton>
        </RightButtonArea>
        <CustomDraggableDialog
          acceptText={<TranslatedMessage id="common.ok" />}
          cancelText={<TranslatedMessage id="common.cancel" />}
          content={<TranslatedMessage id="common.confirmDeleteDescription" />}
          isOpen={isDeleteDialogOpen}
          onAccept={() => handleDelete()}
          onCancel={closeDeleteDialog}
          title=" "
        />
        <CustomDraggableDialog
          title=""
          acceptText={<TranslatedMessage id="common.yes" />}
          cancelText={<TranslatedMessage id="common.no" />}
          content={<TranslatedMessage id="person.validation.confirmEmailChange" />}
          isOpen={isEmailChangeDialogOpened}
          onAccept={handleConfirmEmailChange}
          onCancel={() => setIsEmailChangeDialogOpened(false)}
        />
        {renderCandidateForm()}
      </React.Fragment>
    );
  };

  const renderPersonForm = () => {
    const {
      isFirstnameEmpty,
      isLastnameEmpty,
      isGenderEmpty,
      isBirthDateEmpty,
      isEmailEmpty,
      isNationalityEmpty,
      isPlaceOfOriginEmpty,
      isCantonEmpty,
    } = isRequired;

    return (
      <React.Fragment>
        <Typography variant="h6">
          <TranslatedMessage id="person.section.personalDetails" />
        </Typography>
        <Grid container={true} spacing={4}>
          <GridTextField
            error={isFirstnameEmpty}
            required
            label={GetTranslation(intl, "person.field.firstName")}
            xs={12}
            sm={6}
            md={4}
            onChange={getChangeHandler(PropertyNames.firstName)}
            value={candidate.firstName || ""}
          />
          <GridTextField
            error={isLastnameEmpty}
            required
            label={GetTranslation(intl, "person.field.lastName")}
            xs={12}
            sm={6}
            md={4}
            onChange={getChangeHandler(PropertyNames.lastName)}
            value={candidate.lastName || ""}
          />

          <GenderSelect
            xs={12}
            sm={6}
            md={4}
            error={isGenderEmpty}
            required
            value={candidate.gender}
            onChange={(value) => handleChange({ gender: value })}
          />

          <Grid item={true} xs={12} sm={6} md={4} lg={4} xl={4} style={{ paddingTop: 0, paddingBottom: 0 }}>
            <DatePicker
              label={<TranslatedMessage id="person.field.birthDate" />}
              value={getValidDate(candidate.birthdate)}
              onChange={(date) => handleChange({ birthdate: date ?? undefined })}
              required={true}
              error={isBirthDateEmpty}
            />
          </Grid>
          <Grid item={true} xs={12} sm={6} md={4}>
            <AhvNoTextField
              required={true}
              label={<TranslatedMessage id="person.field.ahvNumber" />}
              onChangeIsValid={handleAhvIsValid}
              onChange={getChangeHandler(PropertyNames.ahvNumber)}
              value={candidate.ahvNumber || ""}
            />
          </Grid>
          <Grid item={true} xs={12} sm={6} md={4}>
            <EmailTextField
              error={isEmailEmpty}
              required={true}
              label={"Email"}
              onChangeIsValid={handleEmailIsValid}
              onChange={getChangeHandler(PropertyNames.emailAddress)}
              value={candidate.emailAddress || ""}
            />
          </Grid>
          <Grid item={true} xs={12} sm={6} md={4}>
            <PhoneNoTextField
              label={GetTranslation(intl, "person.field.phoneNumber")}
              onChangeIsValid={handlePhoneIsValid}
              onChange={getChangeHandler(PropertyNames.phoneNumber)}
              value={candidate.phoneNumber || ""}
            />
          </Grid>
          <Grid item={true} xs={12} sm={6} md={4}>
            <PhoneNoTextField
              label={GetTranslation(intl, "person.field.mobileNumber")}
              onChangeIsValid={handleMobileIsValid}
              onChange={getChangeHandler(PropertyNames.mobileNumber)}
              value={candidate.mobileNumber || ""}
            />
          </Grid>
          <Grid item={true} xs={12} sm={6} md={4}>
            <CiceroNoTextField
              required={false}
              label={<TranslatedMessage id="person.field.ciceroNumber" />}
              onChangeIsValid={handleCiceroIsValid}
              onChange={getChangeHandler(PropertyNames.ciceroNumber)}
              value={candidate.ciceroNumber || ""}
            />
          </Grid>
          <CountrySelect
            xs={12}
            sm={6}
            md={4}
            label={GetTranslation(intl, "person.field.homeCountry")}
            defaultValue={Switzerland}
            value={candidate.nationalityIsSwiss ? Switzerland : candidate.placeOfOrigin}
            onChange={(value) => handleNationalityChanged(value)}
            required
            error={isNationalityEmpty}
          />
          {candidate.nationalityIsSwiss ? (
            <>
              <PlaceOfOriginSelect
                xs={12}
                sm={6}
                md={4}
                value={candidate.placeOfOrigin}
                onChange={(value) => handlePlaceOfOriginChange(value)}
                required
                error={isPlaceOfOriginEmpty}
              />
              <GridTextField
                readOnly={true}
                label={GetTranslation(intl, "candidate.field.canton")}
                xs={12}
                sm={6}
                md={4}
                value={getCantonDisplayValue(candidate.canton)}
                required
                error={isCantonEmpty}
              />
            </>
          ) : (
            <>
              <FormGridCell size="third"></FormGridCell>
              <FormGridCell size="third"></FormGridCell>
            </>
          )}
        </Grid>
      </React.Fragment>
    );
  };

  const renderBusinessDataForm = () => {
    return (
      <React.Fragment>
        <Typography variant="h6">
          <TranslatedMessage id="person.section.businessData" />
        </Typography>
        <Grid container={true} spacing={4}>
          <CompanySelect
            xs={12}
            sm={6}
            md={4}
            label={GetTranslation(intl, "person.field.employer")}
            value={candidate.employerInformation}
            onChange={(value) => handleChange({ employerInformation: value })}
            required={true}
            error={isRequired.isEmployerInformationEmpty}
          />
        </Grid>
      </React.Fragment>
    );
  };

  const renderAddressForm = () => {
    return (
      <React.Fragment>
        <Typography variant="h6">
          <TranslatedMessage id="person.section.address" />
        </Typography>
        <Grid container={true} spacing={4}>
          <AddressFields fieldValues={candidate} fieldsValidation={isRequired} handleChange={handleChange} />
          <Grid item={true} xs={12} sm={6} md={4}>
            {renderLanguageCheckboxList()}
          </Grid>
        </Grid>
      </React.Fragment>
    );
  };

  const renderInvoiceForm = () => {
    return (
      <React.Fragment>
        <Grid item={true} xs={12} sm={6} md={6}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              <TranslatedMessage id="candidate.field.invoiceRecipient" />
            </FormLabel>
            <RadioGroup
              aria-label="contact2"
              name="contact2"
              value={(candidate.privateIsPreferredContact && "private") || "business"}
              onChange={handlePreferredContactChange}
            >
              {renderRadioButtons()}
            </RadioGroup>
          </FormControl>
        </Grid>
      </React.Fragment>
    );
  };

  const renderBusinessForm = () => {
    return (
      <React.Fragment>
        <Typography variant="h6">
          <TranslatedMessage id="person.section.businessData" />
        </Typography>
        <Grid container={true} spacing={4}>
          <Grid item container xs={12}>
            <GridTextField
              label={GetTranslation(intl, "person.field.employer")}
              xs={12}
              sm={6}
              md={4}
              onChange={getChangeHandler(PropertyNames.employer)}
              value={candidate.employer}
            />
          </Grid>
          <GridTextField
            label={GetTranslation(intl, "person.field.employerStreet")}
            xs={12}
            sm={6}
            md={3}
            onChange={getChangeHandler(PropertyNames.employerStreet)}
            value={candidate.employerStreet}
          />
          <GridTextField
            label={GetTranslation(intl, "person.field.employerStreetNumber")}
            xs={12}
            sm={6}
            md={2}
            onChange={getChangeHandler(PropertyNames.employerStreetNumber)}
            value={candidate.employerStreetNumber}
            maxLength={9}
          />
          <GridTextField
            label={GetTranslation(intl, "person.field.employerZipCode")}
            xs={12}
            sm={6}
            md={2}
            onChange={getZipCodeChangeHandler(PropertyNames.employerZipCode, PropertyNames.employerCity)}
            value={candidate.employerZipCode}
            error={isInvalidZipCode(candidate.employerZipCode, candidate.employerCountry)}
          />
          <GridTextField
            label={GetTranslation(intl, "person.field.employerCity")}
            xs={12}
            sm={6}
            md={2}
            onChange={getChangeHandler(PropertyNames.employerCity)}
            value={candidate.employerCity}
          />
          <CountrySelect
            xs={12}
            sm={6}
            md={3}
            label={GetTranslation(intl, "person.field.employerCountry")}
            value={candidate.employerCountry}
            onChange={(value) => handleChange({ employerCountry: value })}
          />
        </Grid>
      </React.Fragment>
    );
  };

  const renderRadioButtons = () => {
    return (
      <React.Fragment>
        <FormControlLabel value="private" control={<Radio />} label={GetTranslation(intl, "common.private")} />
        <FormControlLabel
          className={classes.formControl}
          value="business"
          control={<Radio />}
          label={GetTranslation(intl, "common.business")}
        />
      </React.Fragment>
    );
  };

  const renderLanguageCheckboxList = () => {
    const { isLanguageEmpty } = isRequired;
    return (
      <React.Fragment>
        <FormControl required error={isLanguageEmpty} component="fieldset">
          <FormLabel component="legend">
            <TranslatedMessage id="candidate.field.correspondenceLanguage" />
          </FormLabel>
          <RadioGroup
            aria-label="language"
            name="language"
            value={candidate.correspondenceLanguage}
            onChange={getChangeHandler(PropertyNames.correspondenceLanguage)}
          >
            <FormControlLabel value="DE" control={<Radio />} label={<TranslatedMessage id="common.language.de" />} />
            <FormControlLabel value="FR" control={<Radio />} label={<TranslatedMessage id="common.language.fr" />} />
            <FormControlLabel
              className={classes.formControl}
              value="IT"
              control={<Radio />}
              label={<TranslatedMessage id="common.language.it" />}
            />
          </RadioGroup>
        </FormControl>
      </React.Fragment>
    );
  };

  const getChangeHandler = (propertyName: PropertyNames) => {
    return (event: any) => {
      handleChange({ [propertyName]: event.target.value });
    };
  };

  const handlePlaceOfOriginChange = (option: IPlaceOfOriginOption | null) => {
    handleChange({ placeOfOrigin: option?.value });
    handleChange({ canton: option?.canton });
  };

  // Used for both the private and the employer zipcodes
  const getZipCodeChangeHandler = (zipCodeProperty: PropertyNames, cityProperty: PropertyNames) => {
    return (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newZipCode = event.target.value;
      const zipCodeMatch = ZipCodes.find((zipCode) => zipCode.zipCode === newZipCode);
      handleChange({
        [zipCodeProperty]: newZipCode,
        [cityProperty]: zipCodeMatch ? zipCodeMatch.city : "",
      });
    };
  };

  const getLanguageChangeHandler = () => {
    return (value: string[]) => {
      return handleChange({ languages: value as Language[] });
    };
  };

  const getExamChangeHandler = (role: ExpertRole) => {
    return (selectedElements: ValueType<OptionType>, actionMeta?: ActionMeta<OptionType>) => {
      if ((actionMeta && actionMeta.action === "clear") || !selectedElements) {
        const modifiedRoles = candidate.rolesWithExamsAndCourseTypes
          .filter((rec) => rec.role !== role)
          .concat([{ role: role, examIds: [], courseTypes: [] }]);
        return handleChange({ rolesWithExamsAndCourseTypes: modifiedRoles });
      }

      const selectedElementsArray = selectedElements as OptionType[];

      if (selectedElementsArray) {
        var values = selectedElementsArray.map((selectedElement) => selectedElement.value);
        const modifiedRoles = candidate.rolesWithExamsAndCourseTypes
          .filter((rec) => rec.role !== role)
          .concat([{ role: role, examIds: values, courseTypes: [] }]);
        return handleChange({ rolesWithExamsAndCourseTypes: modifiedRoles });
      }
    };
  };

  const getCourseTypeChangeHandler = (role: ExpertRole) => {
    return (selectedElements: ValueType<OptionType>, actionMeta?: ActionMeta<OptionType>) => {
      if ((actionMeta && actionMeta.action === "clear") || !selectedElements) {
        const modifiedRoles = candidate.rolesWithExamsAndCourseTypes
          .filter((roleExam) => roleExam.role !== role)
          .concat([{ role: role, examIds: [], courseTypes: [] }]);
        return handleChange({ rolesWithExamsAndCourseTypes: modifiedRoles });
      }

      const selectedElementsArray = selectedElements as OptionType[];

      if (selectedElementsArray) {
        var values = selectedElementsArray.map((selectedElement) => CourseType[selectedElement.value as CourseType]);
        const modifiedRoles = candidate.rolesWithExamsAndCourseTypes
          .filter((rec) => rec.role !== role)
          .concat([{ role: role, examIds: [], courseTypes: values }]);
        return handleChange({ rolesWithExamsAndCourseTypes: modifiedRoles });
      }
    };
  };

  const onRolesCreation = (roles: ExpertRole[]) => {
    const newRoles = roles
      .map((role) => ({ role: role, examIds: [], courseTypes: [] }))
      .filter(
        (newRole) =>
          !candidate.rolesWithExamsAndCourseTypes ||
          candidate.rolesWithExamsAndCourseTypes.every((currentRole) => currentRole.role !== newRole.role)
      );
    handleChange({
      rolesWithExamsAndCourseTypes: (candidate.rolesWithExamsAndCourseTypes || []).concat(newRoles).sort(),
    });
  };

  const onRoleDelete = (role: ExpertRole) => {
    handleChange({
      rolesWithExamsAndCourseTypes:
        candidate.rolesWithExamsAndCourseTypes &&
        candidate.rolesWithExamsAndCourseTypes.filter((rec) => rec.role !== role),
    });
  };

  const closeDeleteDialog = () => setIsDeleteDialogOpen(false);

  const handleChange = (value: Partial<CandidateViewModel>) => {
    if (!isNewCandidate && value.emailAddress !== undefined) {
      // Email was changed, we will display a confirm dialog on save.
      setEmailChangeNeedsConfirmation(true);
    }

    setCandidate((prevState) => ({
      ...prevState,
      ...value,
    }));
  };

  const handleCreate = async () => {
    if (handleRequired() && handleCandidateExpertRequired() && handleValidation()) {
      const response = await createCandidate(candidate).unwrap();
      if (response.success) {
        close();
      }
    }
  };

  const handleUpdate = async (checkEmailChangeConfirmation = true) => {
    if (checkEmailChangeConfirmation && emailChangeNeedsConfirmation) {
      setIsEmailChangeDialogOpened(true);
      return;
    }

    if (handleRequired() && handleCandidateExpertRequired() && handleValidation()) {
      const response = await updateCandidate(candidate).unwrap();
      if (response.success) {
        close();
      }
    }
  };

  const handleDelete = async () => {
    var response = await deleteCandidate(candidate.id).unwrap();
    if (response.success) {
      enqueueSnackbar(<TranslatedMessage id="expert.snackbar.successfulDelete" />);
      close();
    }
  };

  const handleExport = async () => {
    exportDebtorNumber(candidate.id).then(() => {
      enqueueSnackbar(<TranslatedMessage id="candidate.snackbar.successfulExport" />);
    });
  }

  const handleCandidateExpertRequired = () => {
    if (candidate.type !== PersonType.Expert) {
      return true;
    }

    const newStatus: RequiredCandidateExpert = {
      isBankAccountEmpty: !candidate.bankAccount,
      hasEmptyRoleArray: candidate.rolesWithExamsAndCourseTypes
        .filter(
          (rec) =>
            rolesWithRequiredExamOrCourseTypeAssignements.includes(rec.role) &&
            (rolesAssignedToCourseTypes.includes(rec.role) ? rec.courseTypes.length === 0 : rec.examIds.length === 0)
        )
        .map((rec) => rec.role),
      hasEmptyRole: candidate.rolesWithExamsAndCourseTypes.some(
        (rec) =>
          rolesWithRequiredExamOrCourseTypeAssignements.includes(rec.role) &&
          (rolesAssignedToCourseTypes.includes(rec.role) ? rec.courseTypes.length === 0 : rec.examIds.length === 0)
      ),
    };

    const values = Object.values(newStatus);
    const hasMissingFields = values.some((value) => value === true);

    if (hasMissingFields) {
      setIsRequired({ ...isRequired, ...newStatus });
    }
    return !hasMissingFields;
  };

  const handleRequired = () => {
    const newStatus: RequiredCandidate = {
      isFirstnameEmpty: !candidate.firstName,
      isLastnameEmpty: !candidate.lastName,
      isGenderEmpty: !candidate.gender,
      isBirthDateEmpty: getValidDate(candidate.birthdate) === null,
      isEmailEmpty: !candidate.emailAddress,
      isStreetEmpty: !candidate.street,
      isStreetNumberEmpty: !candidate.streetNumber,
      isZipCodeEmpty: !candidate.zipCode,
      isCityEmpty: !candidate.city,
      isCountryEmpty: !candidate.country,
      isLanguageEmpty: false,
      isAhvNoEmpty: !candidate.ahvNumber,
      isNationalityEmpty: !candidate.placeOfOrigin,
      isPlaceOfOriginEmpty:
        candidate.nationalityIsSwiss !== undefined && candidate.nationalityIsSwiss && !candidate.placeOfOrigin,
      isCantonEmpty: candidate.nationalityIsSwiss !== undefined && candidate.nationalityIsSwiss && !candidate.canton,
      isEmployerInformationEmpty: !candidate.employerInformation,
    };
    const values = Object.values(newStatus);
    const hasMissingFields = values.some((value) => value === true);

    if (hasMissingFields) {
      setIsRequired({ ...isRequired, ...newStatus });
    }
    return !hasMissingFields;
  };

  const handleValidation = () => {
    const { isAhvValid, isCiceroValid, isEmailValid, isPhoneValid, isMobileValid } = isValid;
    return isAhvValid && isCiceroValid && isEmailValid && isPhoneValid && isMobileValid;
  };

  const handleEmailIsValid = (isEmailValid: boolean) => {
    handleIsValid("isEmailValid", isEmailValid);
  };
  const handleAhvIsValid = (isAhvValid: boolean) => {
    handleIsValid("isAhvValid", isAhvValid);
  };
  const handleCiceroIsValid = (isCiceroValid: boolean) => {
    handleIsValid("isCiceroValid", isCiceroValid);
  };
  const handlePhoneIsValid = (isPhoneValid: boolean) => {
    handleIsValid("isPhoneValid", isPhoneValid);
  };
  const handleMobileIsValid = (isMobileValid: boolean) => {
    handleIsValid("isMobileValid", isMobileValid);
  };

  const handleIsValid = (propertyName: string, isValid: boolean) => {
    setIsValid((prevIsValid) =>
      prevIsValid[propertyName as keyof ValidationFields] !== isValid
        ? {
            ...prevIsValid,
            [propertyName]: isValid,
          }
        : prevIsValid
    );
  };

  const handleConfirmEmailChange = () => {
    setIsEmailChangeDialogOpened(false);
    setEmailChangeNeedsConfirmation(false);
    handleUpdate(false);
  };

  const handleNationalityChanged = (value?: string) => {
    if (value === Switzerland) {
      handleChange({ placeOfOrigin: "" });
      handleChange({ nationalityIsSwiss: true });
    } else {
      handleChange({ canton: "" });
      handleChange({ nationalityIsSwiss: false });
      handleChange({ placeOfOrigin: value });
    }
    handleChange({ nationality: value });
  };

  const handlePreferredContactChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    handleChange({
      preferredContact: value,
      privateIsPreferredContact: value === "private",
    });
  };

  // For the transition from long name to short names of candidate's cantons.
  const getCantonDisplayValue = (canton: string | undefined): string => {
    if (!canton) {
      return "";
    }
    const key = `canton.${canton.toLowerCase()}`;
    if (isTranslationKey(key)) {
      return GetTranslation(intl, key as TranslationKey);
    }
    return canton;
  };
  return (
    <div>
      <Typography variant="h5">
        <TranslatedMessage id="candidate.header" />
      </Typography>
      {renderForm()}
    </div>
  );
};

export default CandidateForm;
